import Stripe from 'stripe'
import { config, httpClient } from '../../../shared'
import {
  AutoRechargeSettings,
  BillingInfo,
  Invoice,
  ProductQuantityConfig,
  ProductQuantityMap,
  ProductWithPrices,
  PromoCode,
} from '../types/index'
import { CancellationReason } from '@bpinternal/const'

const CDM_BACKEND_URL = config.cdmBackendBaseUrl

export class BillingApiClient {
  static async getInvoices(workspaceId: string): Promise<Invoice[]> {
    return await httpClient.callApi<Invoice[]>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/invoices`,
      method: 'GET',
    })
  }

  static async getSubscription(workspaceId: string) {
    return await httpClient.callApi<Stripe.Subscription>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/subscription`,
      method: 'GET',
    })
  }

  static async getSubscriptions(workspaceId: string) {
    return await httpClient.callApi<Stripe.Subscription[]>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/subscriptions`,
      method: 'GET',
    })
  }

  static async getSubscriptionSchedule(workspaceId: string) {
    return await httpClient.callApi<Stripe.SubscriptionSchedule>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/subscription-schedule`,
      method: 'GET',
    })
  }

  static async getBillingInfo(workspaceId: string) {
    return await httpClient.callApi<BillingInfo>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/info`,
      method: 'GET',
    })
  }

  static async updateSubscription(
    workspaceId: string,
    payload: { productQuantityConfig: ProductQuantityConfig; subscriptionId: string }
  ) {
    return await httpClient.callApi<ProductQuantityConfig>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/subscription`,
      data: payload,
      method: 'PUT',
    })
  }

  static async generateCheckoutSession(
    workspaceId: string,
    productQuantityConfig: ProductQuantityMap,
    email: string,
    couponId?: string
  ) {
    return await httpClient.callApi<{ url: string }>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/checkout`,
      data: { productQuantityConfig, email, couponId },
      method: 'POST',
    })
  }

  static async generateCustomerPortalSession(workspaceId: string) {
    return await httpClient.callApi<{ url: string }>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/customer-portal`,
      method: 'POST',
    })
  }

  static async getBillingProducts(workspaceId: string) {
    return await httpClient.callApi<ProductWithPrices[]>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/products`,
      method: 'GET',
    })
  }

  static async getPromoCode(workspaceId: string) {
    const promoCode = await httpClient.callApi<PromoCode>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/promo-code`,
      method: 'GET',
    })

    if (Object.entries(promoCode).length === 0) return null

    return promoCode
  }

  static async searchPromoCode({ query, workspaceId }: { query: string; workspaceId: string }) {
    return await httpClient.callApi<PromoCode>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/promo-code/search`,
      method: 'GET',
      params: { code: query },
    })
  }

  static async setPromoCode({ code, workspaceId }: { code: string; workspaceId: string }) {
    return await httpClient.callApi({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/promo-code`,
      method: 'POST',
      data: { code },
    })
  }

  static async cancelSubscription(workspaceId: string) {
    return await httpClient.callApi({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/cancel`,
      method: 'POST',
    })
  }

  static async saveCancellationReason(workspaceId: string, reasons: CancellationReason[], comment?: string) {
    return await httpClient.callApi({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/cancellation-reason`,
      method: 'POST',
      data: { reasons, comment },
    })
  }

  static async getAutoRechargeSettings(workspaceId: string) {
    const response = await httpClient.callApi<{ settings: AutoRechargeSettings }>({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/auto-recharge/settings`,
      method: 'GET',
    })
    return response?.settings
  }

  static async updateAutoRechargeSettings(workspaceId: string, settings: Partial<AutoRechargeSettings>) {
    return await httpClient.callApi({
      baseURL: CDM_BACKEND_URL,
      url: `/v1/billing/${workspaceId}/auto-recharge/settings`,
      method: 'PATCH',
      data: { settings },
    })
  }
}
